// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parseBoolean } from "@awc/XDL/Utilities";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { css } from "styled-components";

// XNG-Changes: classic does not show brands
const enum fields {
    showBrand = "showBrand",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showBrand]: boolean;
    };
}

type Props = OwnProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h2",
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({
    product,
    // XNG-Changes
    fields,
}) => {
    // XNG-Changes: classic
    const productTitle = parseBoolean(product.properties?.productUsesLongDescription)
        ? product.properties.productLongDescription
        : product.productTitle;
    return (
        <GridContainer {...styles.container}>
            {/* XNG-Changes: fields.showBrand */}
            {product.brand && fields.showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography as="h1" {...styles.titleText} data-test-selector="productDetails_productDescription">
                    {productTitle}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showBrand,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
